// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { LocalStorageKey } from '@par/app/core/model/enums/local-storage-key';
import { PartnerId } from '@par/app/home/models/partner-id';
import { JwtHelperService } from '@auth0/angular-jwt';
import { map } from 'rxjs/operators';
import { DecodedToken } from '@par/app/core/model/decoded-token';

@Injectable({
  providedIn: 'root',
})
export class KaiStorageService {
  private roleId$ = new ReplaySubject<string>();
  private regionId$ = new ReplaySubject<string>();
  private partnerId$ = new ReplaySubject<PartnerId>();
  private accessToken$ = new ReplaySubject<string>();
  private refreshToken$ = new ReplaySubject<string>();

  setRoleId(id: string): void {
    this.roleId$.next(id);
    localStorage.setItem(LocalStorageKey.roleId, id);
  }

  getRoleId(): Observable<string> {
    return this.roleId$.asObservable();
  }

  setRegionId(id: string): void {
    this.regionId$.next(id);
    localStorage.setItem(LocalStorageKey.regionId, id);
  }

  getRegionId(): Observable<string> {
    return this.regionId$.asObservable();
  }

  setPartnerId(id: PartnerId): void {
    this.partnerId$.next(id);
    localStorage.setItem(LocalStorageKey.partnerId, id.toString());
  }

  getPartnerId(): Observable<PartnerId> {
    return this.partnerId$.asObservable();
  }

  setAccessToken(token: string): void {
    this.accessToken$.next(token);
    localStorage.setItem(LocalStorageKey.accessToken, token);
  }

  setRefreshToken(token: string): void {
    this.refreshToken$.next(token);
    localStorage.setItem(LocalStorageKey.refreshToken, token);
  }

  getAccessToken(): Observable<string> {
    return this.accessToken$.asObservable();
  }

  getAccessTokenValue(): string {
    return localStorage.getItem(LocalStorageKey.accessToken);
  }

  getDecodedAccessToken(): Observable<DecodedToken> {
    return this.accessToken$.asObservable().pipe(map((token) => new JwtHelperService().decodeToken(token)));
  }

  getDecodedAccessTokenValue(): DecodedToken {
    return new JwtHelperService().decodeToken(localStorage.getItem(LocalStorageKey.accessToken));
  }

  getUsernameFromToken(): Observable<string> {
    return this.getDecodedAccessToken().pipe(map(token => {
      if (!token) {
        return '';
      }
      return token.user_name;
    }));
  }

  getUserIdFromToken(): Observable<number> {
    return this.getDecodedAccessToken().pipe(map(token => {
      if (!token) {
        return null;
      }
      return token.user_id;
    }));
  }

  getRefreshTokenValue(): string {
    return localStorage.getItem(LocalStorageKey.refreshToken);
  }

  initStorage(): void {
    const roleId = localStorage.getItem(LocalStorageKey.roleId);
    this.roleId$.next(roleId);
    const regionId = localStorage.getItem(LocalStorageKey.regionId);
    this.regionId$.next(regionId);
    const partnerId = +localStorage.getItem(LocalStorageKey.partnerId);
    this.partnerId$.next(partnerId);

    const accessToken = localStorage.getItem(LocalStorageKey.accessToken);
    this.accessToken$.next(accessToken);
    const refreshToken = localStorage.getItem(LocalStorageKey.refreshToken);
    this.refreshToken$.next(refreshToken);
  }

  clearStorage(): void {
    localStorage.removeItem(LocalStorageKey.roleId);
    this.roleId$.next(undefined);
    localStorage.removeItem(LocalStorageKey.regionId);
    this.regionId$.next(undefined);
    localStorage.removeItem(LocalStorageKey.partnerId);
    this.partnerId$.next(undefined);

    localStorage.removeItem(LocalStorageKey.accessToken);
    this.accessToken$.next(undefined);
    localStorage.removeItem(LocalStorageKey.refreshToken);
    this.refreshToken$.next(undefined);
  }
}
